/**
 * Reach, and the two things it is not.
 *
 * This is the number slice 14 exists for, and it is the number slice 2 already
 * got wrong once: reachability was computed as `count(*) FILTER (WHERE email IS
 * NOT NULL)` — a **row** count — while the word beside it said reach. The first
 * real data makes the gap concrete: 531 rows carry an email address and only 526
 * of those addresses are different. A screen advertising 531 and a send
 * delivering 526 looks broken to the customer, and nothing on the screen
 * explained the five.
 *
 * So this panel prints three counts and never lets them touch:
 *
 *   - **Distinct recipients** — distinct people (the owners of the recipients'
 *     keys, since slice 58), the unit the send path reaches once. Somebody reachable by email
 *     *and* by WhatsApp is one recipient here and two addresses below.
 *   - **Distinct addresses**, per channel — `count(distinct email)` or
 *     `count(distinct phone_e164)`. This is the figure the word *reach* may be
 *     printed beside, and `REACH_UNIT_LABEL` is the words used, imported rather
 *     than typed so the screen and the query cannot come to disagree.
 *   - **Rows** — how many `campaign_recipients` rows the period materialised.
 *     Not a lesser number and not deprecated: it is what the send actually
 *     enumerated, and it is here precisely so a reader can see the gap rather
 *     than discover it later.
 *
 * **The headline is not the two channel figures added together, and the sentence
 * saying so is on the screen rather than in this comment.** `emailReach +
 * whatsappReach` double-counts everybody carrying both — which is exactly slice
 * 2's `bothChannels = emailReachable + phoneReachable − contacts`, an expression
 * that mixed units, compiled, and was wrong. The compiler now refuses it: `Reach`
 * and `RecipientReach` are mutually non-assignable one-field objects and a bare
 * `number` is assignable to neither, so the sum cannot reach this component at
 * all. What the compiler cannot do is stop a *reader* performing the addition,
 * and that is what the copy is for.
 *
 * **Zero addresses and no channel are different, and there are three cases
 * rather than two.** A channel this workspace does not send on arrives as `null`
 * and gets `CHANNEL_NOT_SENDING_NOTES`'s sentence, because "0 distinct addresses
 * on WhatsApp" told to a tenant with no Meta integration claims a measurement
 * that was never taken. A channel that *did* send and reached nobody keeps its
 * zero, which is a real and useful negative. Between them sits the common case:
 * a workspace that sends on the channel but ran no campaign on it inside these
 * dates — no recipient rows and nothing accepted — and its zeros are the absence
 * of a send rather than the outcome of one, so it gets a sentence saying which.
 *
 * **Nothing here is a percentage.** A share needs a denominator both figures are
 * over, and the three counts on this panel are over three different populations
 * — people, addresses and rows. Slice 2's `SegmentCounts` header refuses the same
 * arithmetic for the same reason: "526 of 1,287" produces a 41% that is a share
 * of nothing.
 *
 * **Slice 17 trimmed the captions and kept every unit.** The words `distinct
 * addresses`, `distinct recipients` and `rows` are printed against their own
 * figures and nowhere else — a unit that has drifted to another part of the page
 * is not a unit — and both contract explainers still render, because the
 * sentence that keeps a reader from adding the two channel figures together is
 * the entire reason this panel is three counts rather than one. What went from
 * the captions was the restatement: "each counted once however many campaigns
 * and channels reached them" said the same thing
 * `RECIPIENT_REACH_EXPLAINER` says one line below it, in more words.
 */
import {
  REACH_EXPLAINER,
  REACH_UNIT_LABEL,
  RECIPIENT_REACH_EXPLAINER,
  RECIPIENT_REACH_UNIT_LABEL,
  ROW_COUNT_UNIT_LABEL,
  type Channel,
  type ChannelAnalytics,
  type EmailAnalytics,
  type RecipientReach,
  type WhatsAppAnalytics,
} from '@wend/shared';
import { Kicker, MetricPanel } from '@wend/ui';

import { CHANNEL_LABEL } from '../../lib/campaign-display';
import { ChannelNotSendingNote } from './AnalyticsCopy';

function Figure({
  label,
  value,
  caption,
  emphasis = false,
}: {
  label: string;
  value: number;
  caption: string;
  /** The number a person should leave with. Exactly one per panel. */
  emphasis?: boolean;
}) {
  /*
   * Slice 66: the shared `MetricPanel` rather than a mono numeral in the brand
   * gradient — sans tabular digits (mono's dotted zero reads as an 8), the
   * kicker role, the caption as the panel's `sub`. The one figure to leave with
   * (people, across both channels) is the violet panel at the larger size; the
   * per-channel counts are plain. No meter on any of them: an address count and
   * a row count are not shares of anything on this panel, and a bar would
   * invite exactly the comparison the closing sentence warns against.
   */
  return (
    <MetricPanel
      kicker={label}
      figure={{ kind: 'measured', value }}
      figureSize={emphasis ? 'lg' : 'md'}
      sub={caption}
      variant={emphasis ? 'brand' : 'plain'}
      className={emphasis ? 'max-w-xl' : undefined}
    />
  );
}

/**
 * What a distinct address *is* on each channel, said in the channel's own noun.
 *
 * Written out per channel rather than assembled from `CHANNEL_ADDRESS_LABEL`,
 * which is worded for a different sentence ("an email address", "a phone
 * number") and pluralises into nonsense.
 */
const REACH_CAPTION: Readonly<Record<Channel, string>> = {
  email: 'Different email addresses messaged, each counted once.',
  whatsapp: 'Different phone numbers messaged, each counted once.',
};

/**
 * One channel's two counts, side by side, in the units they were counted in.
 *
 * The row count sits beside the address count deliberately. Kept on separate
 * screens they read as the same fact told twice; kept together the difference
 * between them is visible, which is the entire lesson of this panel.
 */
function ChannelReach({ analytics }: { analytics: ChannelAnalytics }) {
  const channel = CHANNEL_LABEL[analytics.channel];
  // Nothing materialised and nothing accepted: no campaign on this channel went
  // out in these dates. Zeros with rows behind them are a measurement and keep
  // their silence — see `AnalyticsChannelPanel`'s header.
  const nothingSent = analytics.recipientRows.rows === 0 && analytics.accepted === 0;

  return (
    <div className="flex flex-col gap-3 pt-2">
      <Kicker rule>{channel}</Kicker>

      {nothingSent && (
        <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
          Nothing went out on {channel} in this period. The zeros are an absence of sends, not a
          send that reached nobody.
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Figure
          label={`${REACH_UNIT_LABEL} · ${channel}`}
          value={analytics.reach.addresses}
          caption={REACH_CAPTION[analytics.channel]}
        />
        <Figure
          label={`${ROW_COUNT_UNIT_LABEL} · ${channel}`}
          value={analytics.recipientRows.rows}
          caption="Recipient rows these campaigns produced. Rows, not people."
        />
      </div>
    </div>
  );
}

export interface AnalyticsReachPanelProps {
  recipientReach: RecipientReach;
  /** `null` means this workspace does not send email — never "zero email". */
  email: EmailAnalytics | null;
  /** `null` means there is no WhatsApp integration here — never "zero WhatsApp". */
  whatsapp: WhatsAppAnalytics | null;
}

export function AnalyticsReachPanel({ recipientReach, email, whatsapp }: AnalyticsReachPanelProps) {
  return (
    <div className="flex flex-col gap-5">
      <Figure
        label={RECIPIENT_REACH_UNIT_LABEL}
        value={recipientReach.recipients}
        caption="People messaged in this period, counted once each."
        emphasis
      />

      <p className="max-w-prose text-[0.9rem] leading-relaxed text-muted">
        {RECIPIENT_REACH_EXPLAINER}
      </p>

      <p className="max-w-prose text-[0.9rem] leading-relaxed text-muted">{REACH_EXPLAINER}</p>

      {email === null ? (
        <div className="border-t border-line pt-4">
          <ChannelNotSendingNote channel="email" />
        </div>
      ) : (
        <ChannelReach analytics={email} />
      )}

      {whatsapp === null ? (
        <div className="border-t border-line pt-4">
          <ChannelNotSendingNote channel="whatsapp" />
        </div>
      ) : (
        <ChannelReach analytics={whatsapp} />
      )}

      <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
        The channel figures are {REACH_UNIT_LABEL} and do not add up to the{' '}
        {RECIPIENT_REACH_UNIT_LABEL} above them: somebody messaged on both channels is two addresses
        here and one person there.
      </p>
    </div>
  );
}
